import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  DollarSign, TrendingDown, Loader2, AlertTriangle, 
  CheckCircle, Info, Zap, PiggyBank
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CostCalculator = ({ phase1Data, className = "" }) => {
  const [costs, setCosts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const calculateCosts = useCallback(async () => {
    if (!phase1Data?.cloud_platform) return;

    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(`${API}/cost-calculator`, {
        cloud_platform: phase1Data.cloud_platform || "AWS",
        compute_preference: phase1Data.tech_stack?.compute_preference || "Containers",
        database_type: phase1Data.data_storage?.database_type || "SQL (PostgreSQL)",
        storage_gb: parseInt(phase1Data.data_storage?.volume?.match(/\d+/)?.[0] || "100"),
        user_volume: phase1Data.scale?.user_volume || "10K - 100K DAU",
        uptime_sla: phase1Data.availability?.uptime_sla || "99.9% (8.76 hours/year)",
        dr_strategy: phase1Data.availability?.dr_strategy || "Warm Standby",
        multi_az: phase1Data.availability?.uptime_sla?.includes("99.9") || true,
      });

      setCosts(response.data);
    } catch (err) {
      setError("Failed to calculate costs");
    } finally {
      setLoading(false);
    }
  }, [phase1Data]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      calculateCosts();
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [calculateCosts]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getBudgetStatus = () => {
    if (!costs || !phase1Data?.budget?.range) return null;

    const budgetRange = phase1Data.budget.range;
    let maxBudget = 0;

    if (budgetRange.includes("< $1,000")) maxBudget = 1000;
    else if (budgetRange.includes("$1,000 - $5,000")) maxBudget = 5000;
    else if (budgetRange.includes("$5,000 - $20,000")) maxBudget = 20000;
    else if (budgetRange.includes("$20,000 - $50,000")) maxBudget = 50000;
    else if (budgetRange.includes("$50,000 - $100,000")) maxBudget = 100000;
    else maxBudget = 200000;

    const percentage = (costs.monthly_estimate / maxBudget) * 100;
    const isWithinBudget = costs.monthly_estimate <= maxBudget;

    return { percentage: Math.min(percentage, 100), isWithinBudget, maxBudget };
  };

  const budgetStatus = getBudgetStatus();

  if (!phase1Data?.cloud_platform) {
    return (
      <Card className={`bg-[#09090b] border-white/5 ${className}`}>
        <CardContent className="p-6">
          <div className="flex items-center gap-3 text-zinc-500">
            <Info className="w-5 h-5" />
            <span className="text-sm">Select a cloud platform to see cost estimates</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading && !costs) {
    return (
      <Card className={`bg-[#09090b] border-white/5 ${className}`}>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-[#00f0ff] animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={`bg-[#09090b] border-white/5 ${className}`}>
        <CardContent className="p-6">
          <div className="flex items-center gap-3 text-red-400">
            <AlertTriangle className="w-5 h-5" />
            <span className="text-sm">{error}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!costs) return null;

  return (
    <Card className={`bg-[#09090b] border-white/5 ${className}`} data-testid="cost-calculator">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[#00ff9d]" />
            Real-time Cost Estimate
          </CardTitle>
          {loading && <Loader2 className="w-4 h-4 text-[#00f0ff] animate-spin" />}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Main Cost Display */}
        <div className="bg-black/30 rounded-sm p-4 border border-white/5">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-zinc-500 text-sm">Monthly Estimate</span>
            <Badge className="bg-[#00f0ff]/10 text-[#00f0ff] border-[#00f0ff]/30">
              {costs.platform}
            </Badge>
          </div>
          <div className="text-3xl font-bold text-white font-mono" data-testid="monthly-cost">
            {formatCurrency(costs.monthly_estimate)}
            <span className="text-sm text-zinc-500 font-normal">/mo</span>
          </div>
          <div className="text-sm text-zinc-500 mt-1">
            ~{formatCurrency(costs.annual_estimate)}/year
          </div>
        </div>

        {/* Budget Status */}
        {budgetStatus && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-500">Budget Status</span>
              <span className={budgetStatus.isWithinBudget ? "text-[#00ff9d]" : "text-red-400"}>
                {budgetStatus.isWithinBudget ? (
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" /> Within Budget
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" /> Over Budget
                  </span>
                )}
              </span>
            </div>
            <Progress 
              value={budgetStatus.percentage} 
              className={`h-2 ${budgetStatus.isWithinBudget ? "[&>div]:bg-[#00ff9d]" : "[&>div]:bg-red-500"}`}
            />
            <div className="flex justify-between text-xs text-zinc-600">
              <span>$0</span>
              <span>{formatCurrency(budgetStatus.maxBudget)}</span>
            </div>
          </div>
        )}

        {/* Cost Breakdown */}
        <div className="space-y-2">
          <div className="text-sm font-medium text-zinc-400 mb-2">Cost Breakdown</div>
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {Object.entries(costs.breakdown).map(([service, cost]) => (
              <div key={service} className="flex items-center justify-between text-sm">
                <span className="text-zinc-500 truncate mr-2">{service}</span>
                <span className="text-white font-mono">{formatCurrency(cost)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Optimization Tips */}
        {costs.optimization_tips?.length > 0 && (
          <div className="space-y-2 pt-3 border-t border-white/5">
            <div className="text-sm font-medium text-[#00f0ff] flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Optimization Tips
            </div>
            <ul className="space-y-1">
              {costs.optimization_tips.slice(0, 3).map((tip, i) => (
                <li key={i} className="text-xs text-zinc-500 flex items-start gap-2">
                  <span className="text-[#00f0ff] mt-0.5">•</span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Potential Savings */}
        {costs.potential_savings?.total_potential_savings > 0 && (
          <div className="bg-[#00ff9d]/5 border border-[#00ff9d]/20 rounded-sm p-3">
            <div className="flex items-center gap-2 text-[#00ff9d] text-sm font-medium mb-1">
              <PiggyBank className="w-4 h-4" />
              Potential Savings
            </div>
            <div className="text-xs text-zinc-400">
              Save up to <span className="text-[#00ff9d] font-mono font-semibold">
                {formatCurrency(costs.potential_savings.total_potential_savings)}
              </span>/mo with Reserved Instances & Spot
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="text-xs text-zinc-600 italic pt-2">
          {costs.disclaimer}
        </div>
      </CardContent>
    </Card>
  );
};
